import React, { useState, useEffect } from 'react';
import { GoArrowLeft, GoArrowRight } from 'react-icons/go';

import {
  PageArrow,
  PaginationContainer,
  PaginationSection,
  CollapsePaginationContainer,
  CollapseButton,
  CollapseButtonSection,
  PaginationSelect,
} from 'src/pages/CatalogPage/components/PaginationBar/PaginationBar.style';
import { ICatalogSearch } from 'src/pages/CatalogPage/CatalogPage.i';
import { GiHamburgerMenu } from 'react-icons/gi';
import { AiOutlineClose } from 'react-icons/ai';
import { Item } from '@react-stately/collections';

interface IPaginationBar {
  loc: 'top' | 'bottom';
  searchBusy: boolean;
  pageNumber: number;
  sortBy: string;
  perPage: number;
  updateSearch: ICatalogSearch;
  totalResults: number;
  auctionTypeId: number;
}

export const PaginationBar = (props: IPaginationBar) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(true);

  function pageSelect(e): void {
    if(parseInt(e.value) !== props.pageNumber) {
      props.updateSearch({
        pageNumber: parseInt(e.value),
      });
    }
  }

  const totalPages = Math.ceil(props.totalResults / props.perPage);

  function nextPage(): void {
    if(props.pageNumber + 1 <= totalPages) {
      props.updateSearch({
        pageNumber: props.pageNumber + 1,
      });
    }
  }

  function prevPage(): void {
    if(props.pageNumber - 1 > 0) {
      props.updateSearch({
        pageNumber: props.pageNumber - 1,
      });
    }
  }

  function perPageChange(e): void {
    if(parseInt(e.value) !== props.perPage) {
      props.updateSearch({
        pageNumber: 1,
        perPage: parseInt(e.value),
      });
    }
  }

  function sortChange(e): void {
    if(e.value !== props.sortBy) {
      props.updateSearch({
        pageNumber: 1, sortBy: e.value,
      });
    }
  }

  // Setup vars to show limits to user
  const limitstart = (props.pageNumber - 1) * props.perPage + 1;
  let limittop = limitstart + props.perPage - 1;
  if(props.totalResults < 50 || limittop > props.totalResults) {
    limittop = props.totalResults;
  }
  const pagesDropdown = [];
  for(let i = 1; i <= totalPages; i++) {
    pagesDropdown.push(i);
  }
  let sortByOptions = [
    { value: 'LotNumber,ASC', label: 'Lot # (ASC)' },
    { value: 'LotNumber,DESC', label: 'Lot # (DESC)' },
    { value: 'CurrentBid,ASC', label: 'Current Bid (ASC)' },
    { value: 'CurrentBid,DESC', label: 'Current Bid (DESC)' },
    { value: 'LowEst,ASC', label: 'Est. Low Price (ASC)' },
    { value: 'LowEst,DESC', label: 'Est. Low Price (DESC)' },
    { value: 'HighEst,ASC', label: 'Est. High Price (ASC)' },
    { value: 'HighEst,DESC', label: 'Est. High Price (DESC)' },
    { value: 'ManufacturerName,ASC', label: 'Manufacturer (ASC)' },
    { value: 'ManufacturerName,DESC', label: 'Manufacturer (DESC)' },
    { value: 'ModelDescription,ASC', label: 'Model (ASC)' },
    { value: 'ModelDescription,DESC', label: 'Model (DESC)' },
    { value: 'ItemInterest,ASC', label: 'Activity (ASC)' },
    { value: 'ItemInterest,DESC', label: 'Activity (DESC)' },
  ];

  // Remove estimated price sort options for timed auctions
  if(props.auctionTypeId === TIMED_AUCTION_TYPE_ID) {
    sortByOptions = sortByOptions.filter((x) => !x.label.includes('Est. '));
  } else {
    sortByOptions = sortByOptions.filter((x) => !x.label.includes('Bid '));
    sortByOptions = sortByOptions.filter((x) => !x.label.includes('Activity '));
  }

  const perPageOptions = [
    { value: '50', label: '50 Items' },
    { value: '100', label: '100 Items' },
    { value: '150', label: '150 Items' },
    { value: '200', label: '200 Items' },
  ];

  const handleOpen = () => {
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    let timer1;
    if(isOpen) {
      timer1 = setTimeout(() => setIsAnimating(false), 500);
    } else {
      setIsAnimating(true);
    }
    return () => clearTimeout(timer1);
  }, [isOpen]);

  return (
    <div>
      <PaginationContainer>
        <PaginationSection>
          {limitstart}-{limittop} of {props.totalResults}
          <PageArrow isDisabled={props.searchBusy || props.pageNumber == 1} onPress={prevPage}>
            <GoArrowLeft />
          </PageArrow>
          <PageArrow isDisabled={totalPages === 0 || props.searchBusy || props.pageNumber === totalPages} onPress={nextPage}>
            <GoArrowRight />
          </PageArrow>
        </PaginationSection>
        <CollapseButtonSection>
          <CollapseButton onPress={handleOpen}>
            {isOpen
              ? <AiOutlineClose/> : <GiHamburgerMenu/>
            }
          </CollapseButton>
        </CollapseButtonSection>
      </PaginationContainer>
      <CollapsePaginationContainer show={isOpen} style={{ overflow: isAnimating ? 'hidden' : 'visible' }}>
        <div>
          <PaginationSection width={68}>
            <PaginationSelect
              label='Page'
              value = {props.pageNumber.toString()}
              onChange = {(page) => pageSelect({ value: page })}
              inline
              inverted
              noMargin
              noOptionsBorder
              useBubbleShield = {false}
              isDisabled = {props.searchBusy || totalPages === 0}
            >
              {pagesDropdown.map((page) => (
                <Item key = {page}>
                  {page.toString()}
                </Item>
              ))}
            </PaginationSelect>
          </PaginationSection>
          <PaginationSection width={175}>
            <PaginationSelect
              label = 'Sort By'
              value = {sortByOptions.find((opt) => opt.value === props.sortBy)?.value}
              onChange = {(value) => sortChange({ value })}
              inline
              inverted
              noMargin
              noOptionsBorder
              useBubbleShield = {false}
            >
              {sortByOptions.map((sortBy) => (
                <Item key = {sortBy.value}>{sortBy.label}</Item>
              ))}
            </PaginationSelect>
          </PaginationSection>
          <PaginationSection width={150}>
            <PaginationSelect
              label = 'Per Page'
              value = {perPageOptions.find((opt) => parseInt(opt.value) === props.perPage)?.value}
              onChange = {(value) => perPageChange({ value })}
              inline
              inverted
              noMargin
              noOptionsBorder
              useBubbleShield = {false}
            >
              {perPageOptions.map((perPage) => (
                <Item key = {perPage.value}>{perPage.label}</Item>
              ))}
            </PaginationSelect>
          </PaginationSection>
        </div>
      </CollapsePaginationContainer>
    </div>
  );
};
